import React, { ReactNode } from "react";

import { Span } from "@vp/swan";
import { Translations } from "../localization/index";

interface NestedStringObject {
  [key: string]: string | NestedStringObject
}

interface LocalizationProviderProps {
  children: ReactNode
  localizations: { [key: string]: unknown }
}
interface UseLocalizationsValue {
  t: (key: Translations, tokens?: { [key: string]: string | React.ReactNode }) => string | React.ReactElement
}

export const localizationContext = React.createContext<UseLocalizationsValue>({
  t: () => {
    return "";
  },
});
export const LocalizationsProvider: React.FC<LocalizationProviderProps> = ({ children, localizations }) => {
  const useLocalizationsValue: UseLocalizationsValue = {
    t: (key: Translations, tokens: { [key: string]: string | React.ReactNode } = {}) => {
      const localizedText = getLocalizationForKey(localizations as NestedStringObject, key);
      if (!localizedText) {
        console.error(`Localization for key ${key} not found`);
        return key;
      }

      const parts = localizedText.split(/{{\s*(.*?)\s*}}/g);
      const renderedParts = parts.map(part => tokens[part] || part);

      const isReactNode = renderedParts.some(part => React.isValidElement(part));
      const renderedPartsWithKeys = isReactNode
        ? parts.map((part, index) => {
          const value = tokens[part] || part;

          if (React.isValidElement(value)) {
            return React.cloneElement(value, { key: `part-${index}` });
          }

          return <React.Fragment key={`part-${index}`}>{value}</React.Fragment>;
        })
        : null;

      return isReactNode ? <Span>{renderedPartsWithKeys}</Span> : renderedParts.join("");
    },
  };
  return <localizationContext.Provider value={useLocalizationsValue}>{children}</localizationContext.Provider>;
};

export const useLocalization = (): UseLocalizationsValue => {
  return React.useContext(localizationContext);
};

const getLocalizationForKey = (localizations: NestedStringObject, path: string): string | undefined => {
  return path.split(".").reduce<NestedStringObject | undefined>((currentLocalizationObj, pathSegment) => {
    if (currentLocalizationObj && typeof currentLocalizationObj === "object" && pathSegment in currentLocalizationObj) {
      return currentLocalizationObj[pathSegment] as NestedStringObject;
    }
    return undefined;
  }, localizations) as string | undefined;
};
